import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Repeat2, Quote } from 'lucide-react';
import { useSession } from '@/lib/auth-client';
import { api } from '@/lib/api';
import { formatCount } from '@/lib/format';

interface EngagementBarProps {
  postId: string;
  likeCount: number;
  replyCount: number;
  repostCount: number;
  quoteCount: number;
  hasLiked?: boolean;
  hasReposted?: boolean;
}

export function EngagementBar({
  postId,
  likeCount,
  replyCount,
  repostCount,
  quoteCount,
  hasLiked,
  hasReposted,
}: EngagementBarProps) {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(hasLiked ?? false);
  const [localLikeCount, setLocalLikeCount] = useState(likeCount);
  const [reposted, setReposted] = useState(hasReposted ?? false);
  const [localRepostCount, setLocalRepostCount] = useState(repostCount);

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!session?.user) {
      navigate('/login');
      return;
    }
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLocalLikeCount((c) => (wasLiked ? c - 1 : c + 1));
    try {
      if (wasLiked) {
        await api.delete(`/posts/${postId}/like`);
      } else {
        await api.post(`/posts/${postId}/like`);
      }
    } catch {
      setLiked(wasLiked);
      setLocalLikeCount((c) => (wasLiked ? c + 1 : c - 1));
    }
  };

  const handleRepost = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!session?.user) {
      navigate('/login');
      return;
    }
    const wasReposted = reposted;
    setReposted(!wasReposted);
    setLocalRepostCount((c) => (wasReposted ? c - 1 : c + 1));
    try {
      if (wasReposted) {
        await api.delete(`/posts/${postId}/repost`);
      } else {
        await api.post(`/posts/${postId}/repost`);
      }
    } catch {
      setReposted(wasReposted);
      setLocalRepostCount((c) => (wasReposted ? c + 1 : c - 1));
    }
  };

  const handleReply = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/post/${postId}`);
  };

  return (
    <div className="flex items-center gap-1 mt-3">
      <button
        onClick={handleReply}
        className="flex items-center gap-1.5 min-touch px-2.5 py-1.5 -ml-2.5 rounded-lg hover:bg-neon-ghost transition-all duration-200 group text-muted hover:text-neon"
      >
        <MessageCircle className="w-[15px] h-[15px]" strokeWidth={1.5} />
        <span className="text-xs font-display">{formatCount(replyCount)}</span>
      </button>

      <button
        onClick={handleRepost}
        className={`flex items-center gap-1.5 min-touch px-2.5 py-1.5 rounded-lg transition-all duration-200 group ${
          reposted
            ? 'text-repost bg-repost-glow'
            : 'text-muted hover:text-repost hover:bg-repost-glow'
        }`}
      >
        <Repeat2 className="w-[15px] h-[15px]" strokeWidth={1.5} />
        <span className="text-xs font-display">{formatCount(localRepostCount)}</span>
      </button>

      <button
        onClick={handleLike}
        className={`flex items-center gap-1.5 min-touch px-2.5 py-1.5 rounded-lg transition-all duration-200 group ${
          liked
            ? 'text-coral bg-coral-glow'
            : 'text-muted hover:text-coral hover:bg-coral-glow'
        }`}
      >
        <Heart
          className="w-[15px] h-[15px]"
          strokeWidth={1.5}
          fill={liked ? 'currentColor' : 'none'}
        />
        <span className="text-xs font-display">{formatCount(localLikeCount)}</span>
      </button>

      {quoteCount > 0 && (
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 text-muted">
          <Quote className="w-[15px] h-[15px]" strokeWidth={1.5} />
          <span className="text-xs font-display">{formatCount(quoteCount)}</span>
        </div>
      )}
    </div>
  );
}
